import { useNavigate } from 'react-router-dom'
import { useEffect, useState, useMemo } from 'react'
import toast from 'react-hot-toast'
import { ClubManagementData } from '../hooks/useClubManagementData'
import {
  useGetClubStaff,
  useGetClubMembersByState,
  useGetClubSettings,
  useAssignOrganizer,
  useRemoveOrganizer,
} from '@/services/club'
import { useSearchPlayerAddToClub } from '@/services'
import { ClubStaff, ClubMembersByStateRequest } from '@/types'
import { useDebounce } from '@/hooks/useDebounce'
import { useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { useCanUseClubCapabilities } from '@/hooks/useCanUseClubCapabilities'
import { useClubCapabilityRoles } from '@/services/clubCapabilities'
import { RoleBadge } from './ui'
import { ManagerRolesModal } from './ManagerRolesModal'

const ORGANIZER_ROLE_ID = 7338457602

/** Which role the add-staff search is adding. */
type AddingAs = 'ORGANIZER' | 'CLUB_STAFF'

interface ClubOverviewTabProps {
  club: ClubManagementData
  isDirector?: boolean
}

export const ClubOverviewTab = ({ club, isDirector = false }: ClubOverviewTabProps) => {
  const navigate = useNavigate()
  const { user } = useAppSelector(userSelector)
  const { mutate: getClubStaff, data: resGetClubStaff } = useGetClubStaff()
  const { mutate: assignOrganizer, isLoading: isAssigning } = useAssignOrganizer()
  const { mutate: removeOrganizer, isLoading: isRemoving } = useRemoveOrganizer()
  const [staffs, setStaffs] = useState<ClubStaff[]>([])
  const [maxOrganizerCount, setMaxOrganizerCount] = useState<number>(0)
  const [maxStaffCount, setMaxStaffCount] = useState<number>(0)
  const [staffRoleId, setStaffRoleId] = useState<number | null>(null)
  const [confirmRemoveUserId, setConfirmRemoveUserId] = useState<number | null>(null)
  const [addingAs, setAddingAs] = useState<AddingAs | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedQuery = useDebounce(searchQuery, 200)

  useEffect(() => {
    getClubStaff(club.clubId)
  }, [club.clubId, getClubStaff])

  useEffect(() => {
    if (resGetClubStaff) {
      const directors = resGetClubStaff.result.directors.map((item) => ({
        ...item,
        isDirectors: true,
      }))
      const organizers = resGetClubStaff.result.organizers
      // Only listed to directors and organizers; the server sends an empty list to anyone else.
      const frontDesk = (resGetClubStaff.result.staff ?? []).map((item) => ({ ...item, isFrontDesk: true }))
      setStaffs([...directors, ...organizers, ...frontDesk])
      setMaxOrganizerCount(resGetClubStaff.result.maxOrganizerCount)
      setMaxStaffCount(resGetClubStaff.result.maxStaffCount ?? 0)
      setStaffRoleId(resGetClubStaff.result.staffRoleId ?? null)
    }
  }, [resGetClubStaff])

  const organizerCount = staffs.filter((s) => !s.isDirectors && !s.isFrontDesk).length
  const atOrganizerLimit = maxOrganizerCount > 0 && organizerCount >= maxOrganizerCount
  const frontDeskCount = staffs.filter((s) => s.isFrontDesk).length
  const atFrontDeskLimit = maxStaffCount > 0 && frontDeskCount >= maxStaffCount

  const staffUserIds = useMemo(() => staffs.map((s) => s.userId), [staffs])

  // RBAC capability roles, attached per manager (no separate list). Empty unless
  // the viewer is internal/flagged and the manager actually holds a role.
  const canUseCapabilities = useCanUseClubCapabilities()
  const { data: capabilityRoles } = useClubCapabilityRoles(club.clubId, canUseCapabilities)
  const rolesByUser = useMemo(() => new Map((capabilityRoles ?? []).map((m) => [m.userId, m.roles])), [capabilityRoles])

  const searchEnabled = addingAs !== null && debouncedQuery.length >= 2

  const { data: searchResults } = useSearchPlayerAddToClub({
    clubId: searchEnabled ? String(club.clubId) : undefined,
    query: searchEnabled ? debouncedQuery : '*',
    limit: 10,
    offset: 0,
    includeUnclaimedPlayers: true,
    filter: {
      lat: user?.addresses?.[0]?.latitude || null,
      lng: user?.addresses?.[0]?.longitude || null,
    },
    excludeClubMembers: {
      clubId: String(club.clubId),
      roles: ['ORGANIZER'],
    },
  })

  const filteredResults = useMemo(() => {
    if (!searchEnabled) return []
    const hits = searchResults?.result?.hits ?? []
    return hits.filter((p) => !staffUserIds.includes(p.id))
  }, [searchResults, staffUserIds, searchEnabled])

  const handleAssign = (userId: number) => {
    const asFrontDesk = addingAs === 'CLUB_STAFF'
    const roleId = asFrontDesk ? staffRoleId : ORGANIZER_ROLE_ID
    if (roleId == null) return
    const label = asFrontDesk ? 'front desk' : 'organizer'
    assignOrganizer(
      { clubId: club.clubId, roleId, userId },
      {
        onSuccess: () => {
          toast.success(asFrontDesk ? 'Front desk added' : 'Organizer added')
          getClubStaff(club.clubId)
          setAddingAs(null)
          setSearchQuery('')
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || `Failed to add ${label}`)
        },
      }
    )
  }

  const handleRemoveOrganizer = (staff: ClubStaff) => {
    removeOrganizer(
      { clubId: club.clubId, roleId: staff.roleId, userId: staff.userId },
      {
        onSuccess: () => {
          toast.success(staff.isFrontDesk ? 'Front desk removed' : 'Organizer removed')
          getClubStaff(club.clubId)
          setConfirmRemoveUserId(null)
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || 'Failed to remove')
        },
      }
    )
  }

  // Fetch pending invites to break down by type
  const pendingPayload = useMemo<ClubMembersByStateRequest>(
    () => ({ state: 'PENDING_INVITE', limit: 200, offset: 0 }),
    []
  )
  const unclaimedPayload = useMemo<ClubMembersByStateRequest>(
    () => ({ state: 'UNCLAIMED_INVITE', limit: 200, offset: 0 }),
    []
  )

  const { data: pendingData } = useGetClubMembersByState(club.clubId, pendingPayload)
  const { data: unclaimedData } = useGetClubMembersByState(club.clubId, unclaimedPayload)

  // Fetch settings to get maxOutstandingWebInvites
  const { data: settingsData } = useGetClubSettings(club.clubId)
  const maxWebInvites: number | undefined = settingsData?.result?.settings?.maxOutstandingWebInvites

  // Break down invites by type
  const inviteBreakdown = useMemo(() => {
    const pendingMembers = pendingData?.results?.[0]?.members ?? []
    const pendingTotal = pendingData?.results?.[0]?.total ?? 0
    const unclaimedTotal = unclaimedData?.results?.[0]?.total ?? 0

    const webInvites = pendingMembers.filter((m) => m.invitationType === 'INVITATION').length
    const csvInvites = pendingMembers.filter((m) => m.invitationType === 'INVITATION_CSV').length
    const partnerInvites = pendingMembers.filter((m) => m.invitationType === 'PARTNER_INVITE').length
    const otherInvites = pendingTotal - webInvites - csvInvites - partnerInvites

    return {
      total: pendingTotal + unclaimedTotal,
      web: webInvites,
      csv: csvInvites,
      partner: partnerInvites,
      other: otherInvites > 0 ? otherInvites : 0,
      unclaimed: unclaimedTotal,
    }
  }, [pendingData, unclaimedData])

  const webExceedsMax = maxWebInvites !== undefined && inviteBreakdown.web >= maxWebInvites

  return (
    <div className="space-y-4">
      {/* Staff */}
      <section>
        <h4 className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2">Staff</h4>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {staffs.length > 0 ? (
            staffs.map((staff, index) => (
              <div key={index} className="flex items-center gap-1.5">
                {confirmRemoveUserId === staff.userId ? (
                  <span className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-500">Remove {staff.name}?</span>
                    <button
                      onClick={() => handleRemoveOrganizer(staff)}
                      disabled={isRemoving}
                      className="text-red-600 hover:text-red-800 font-medium"
                    >
                      {isRemoving ? '...' : 'Yes'}
                    </button>
                    <button
                      onClick={() => setConfirmRemoveUserId(null)}
                      className="text-gray-500 hover:text-gray-700 font-medium"
                    >
                      No
                    </button>
                  </span>
                ) : (
                  <>
                    <button
                      onClick={() => navigate(`/dashboard/player/${staff.userId}`)}
                      className="text-gray-700 text-sm hover:text-primary-600 hover:underline"
                    >
                      {staff.name}
                    </button>
                    <RoleBadge role={staff.isDirectors ? 'DIRECTOR' : staff.isFrontDesk ? 'FRONT DESK' : 'ORGANIZER'} />
                    {(rolesByUser.get(staff.userId)?.length ?? 0) > 0 && <RoleBadge role="COACH" />}
                    <ManagerRolesModal name={staff.name} roles={rolesByUser.get(staff.userId) ?? []} />
                    {isDirector && !staff.isDirectors && staff.userId !== user?.id && (
                      <button
                        onClick={() => setConfirmRemoveUserId(staff.userId)}
                        className="text-gray-300 hover:text-red-500 transition-colors ml-0.5"
                        title={staff.isFrontDesk ? 'Remove front desk' : 'Remove organizer'}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </div>
            ))
          ) : (
            <span className="text-gray-400 text-sm">Loading...</span>
          )}
        </div>

        {/* Add Organizer / Front desk */}
        {isDirector && (
          <div className="mt-3">
            {addingAs === null ? (
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setAddingAs('ORGANIZER')}
                  disabled={atOrganizerLimit}
                  className={`text-xs font-medium ${
                    atOrganizerLimit ? 'text-gray-300 cursor-not-allowed' : 'text-primary-600 hover:text-primary-500'
                  }`}
                  title={atOrganizerLimit ? `Maximum of ${maxOrganizerCount} organizers reached` : undefined}
                >
                  + Add Organizer
                </button>
                {staffRoleId != null && (
                  <button
                    onClick={() => setAddingAs('CLUB_STAFF')}
                    disabled={atFrontDeskLimit}
                    className={`text-xs font-medium ${
                      atFrontDeskLimit ? 'text-gray-300 cursor-not-allowed' : 'text-primary-600 hover:text-primary-500'
                    }`}
                    title={
                      atFrontDeskLimit
                        ? `Maximum of ${maxStaffCount} front desk accounts reached`
                        : 'Front desk can scan members’ benefit codes in the DUPR app and mark them redeemed. Nothing else.'
                    }
                  >
                    + Add Front Desk
                  </button>
                )}
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name..."
                    autoFocus
                    className="text-sm border border-gray-300 rounded px-2 py-1 w-48 focus:outline-none focus:border-primary-500"
                  />
                  <button
                    onClick={() => {
                      setAddingAs(null)
                      setSearchQuery('')
                    }}
                    className="text-gray-400 hover:text-gray-600 text-xs"
                  >
                    Cancel
                  </button>
                </div>

                {debouncedQuery.length >= 2 && filteredResults.length > 0 && (
                  <div className="absolute z-20 bg-white border border-gray-200 rounded shadow-lg w-72 max-h-48 overflow-y-auto">
                    {filteredResults.map((player) => (
                      <div
                        key={player.id}
                        className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="min-w-0 flex-1">
                          <a
                            href={`/dashboard/player/${player.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-gray-900 hover:text-primary-600 hover:underline font-medium truncate block"
                          >
                            {player.fullName}
                          </a>
                          <span className="text-xs text-gray-400 truncate block">
                            {[player.referralCode || player.duprId, player.shortAddress].filter(Boolean).join(' · ')}
                          </span>
                        </div>
                        <button
                          onClick={() => handleAssign(player.id)}
                          disabled={isAssigning}
                          className="text-xs font-medium text-primary-600 hover:text-primary-500 ml-2 flex-shrink-0"
                        >
                          {isAssigning ? '...' : 'Add'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {debouncedQuery.length >= 2 && filteredResults.length === 0 && (
                  <div className="absolute z-20 bg-white border border-gray-200 rounded shadow-lg w-72 px-3 py-2">
                    <span className="text-xs text-gray-400">No results found</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Join Type */}
      {club.clubDetails && (
        <section className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Join Type</span>
          <span className="text-gray-900 font-semibold text-sm">
            {club.clubDetails.clubJoinType?.replace('_', ' ') || 'Open'}
          </span>
        </section>
      )}

      {/* Active Members */}
      <section className="flex items-center justify-between">
        <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Active Members</span>
        <span className="text-gray-900 font-semibold">{club.clubMemberCount}</span>
      </section>

      {/* Pending Members breakdown */}
      <section>
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Pending Invites</span>
          <span className="text-gray-900 font-semibold">{inviteBreakdown.total}</span>
        </div>
        {inviteBreakdown.total > 0 && (
          <div className="mt-2 ml-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-xs">Web invites</span>
              <span className={`text-xs font-medium ${webExceedsMax ? 'text-red-600' : 'text-gray-600'}`}>
                {inviteBreakdown.web}
                {maxWebInvites !== undefined && (
                  <span className={webExceedsMax ? 'text-red-400' : 'text-gray-400'}>/{maxWebInvites}</span>
                )}
              </span>
            </div>
            {inviteBreakdown.csv > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-xs">CSV invites</span>
                <span className="text-xs font-medium text-gray-600">{inviteBreakdown.csv}</span>
              </div>
            )}
            {inviteBreakdown.partner > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-xs">Partner invites</span>
                <span className="text-xs font-medium text-gray-600">{inviteBreakdown.partner}</span>
              </div>
            )}
            {inviteBreakdown.unclaimed > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-xs">Unclaimed</span>
                <span className="text-xs font-medium text-orange-600">{inviteBreakdown.unclaimed}</span>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Active Restrictions */}
      {club.restrictionsData && club.hasRestrictions && (
        <section className="border-t border-red-100 pt-3">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-1.5 mb-1">
                <svg className="w-3.5 h-3.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <span className="text-red-600 font-medium text-xs">Active Restrictions</span>
              </div>
              {/* The staff wording the server authored for each restriction — what this club has
                  to do about it, rather than the name of the rule. */}
              <ul className="text-xs text-gray-600 ml-5 list-none">
                {(club.restrictionsData as { text?: { text?: string }; urn: string }[]).map((restriction) => (
                  <li key={restriction.urn}>{restriction.text?.text || restriction.urn}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
